<template>
  <main dir="rtl" class="min-h-screen bg-slate-50 text-slate-900">
    <header class="bg-white border-b border-slate-200 sticky top-0 z-30">
      <div class="max-w-[1600px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="bg-primary p-2 rounded-lg text-white">
            <span class="material-icons-round">school</span>
          </div>
          <h1 class="text-xl font-bold">جدول الطلاب الأسبوعي</h1>
        </div>
        <div class="flex items-center gap-4 flex-grow md:flex-grow-0">
          <div class="relative flex-grow min-w-[200px]">
            <span class="absolute right-3 top-1/2 -translate-y-1/2 material-icons-round text-slate-400">search</span>
            <input
              v-model="searchQuery"
              class="w-full pr-10 pl-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-primary focus:border-primary"
              placeholder="البحث في فترات الطالب..."
              type="text"
            />
          </div>
          <select
            v-model="selectedSchedule"
            class="rounded-lg border-slate-200 bg-slate-50 focus:ring-primary"
          >
            <option value="">كل الجداول</option>
            <option v-for="schedule in scheduleOptions" :key="schedule.id" :value="schedule.id">
              {{ schedule.title }}
            </option>
          </select>
          <select
            v-model="selectedTeacher"
            class="rounded-lg border-slate-200 bg-slate-50 focus:ring-primary"
          >
            <option value="">كل المعلمين</option>
            <option v-for="teacher in teacherOptions" :key="teacher.id" :value="teacher.id">
              {{ teacher.name }}
            </option>
          </select>
          <div class="relative min-w-[220px]">
            <span class="absolute right-3 top-1/2 -translate-y-1/2 material-icons-round text-slate-400">person_search</span>
            <input
              v-model="studentSearchQuery"
              class="w-full pr-10 pl-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-primary focus:border-primary"
              placeholder="ابحث عن الطالب بالاسم أو الرقم..."
              type="text"
            />
          </div>
          <select
            v-model="selectedStudent"
            class="rounded-lg border-slate-200 bg-slate-50 focus:ring-primary"
          >
            <option value="" disabled>اختر الطالب</option>
            <option v-for="student in studentOptions" :key="student.id" :value="student.id">
              {{ student.full_name }}
            </option>
          </select>
        </div>
      </div>
    </header>

    <section class="max-w-[1600px] mx-auto p-6">
      <div v-if="loading" class="bg-white border border-slate-200 rounded-xl p-5 text-center text-slate-600 mb-6">
        جاري تحميل جدول الطالب الأسبوعي...
      </div>
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 mb-6">
        {{ errorMessage }}
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">groups</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">إجمالي الطلاب</p>
            <p class="text-lg font-bold">{{ studentOptions.length }} طالب</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">event_note</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">الفترات المعروضة</p>
            <p class="text-lg font-bold">{{ filteredLessons.length }} فترة</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">history</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">ساعات الطالب</p>
            <p class="text-lg font-bold">{{ totalHoursPerWeek.toFixed(1) }} ساعة/أسبوع</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">person</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">الطالب المختار</p>
            <p class="text-sm font-bold">{{ selectedStudentName || '-' }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="schedule-grid border-b border-slate-200 bg-slate-50">
          <div class="p-4 border-l border-slate-200 font-bold text-center text-slate-500">الصلاة</div>
          <div
            v-for="weekday in weekdays"
            :key="weekday.id"
            class="p-4 border-l border-slate-200 font-bold text-center"
          >
            {{ weekday.label }}
          </div>
        </div>

        <div class="overflow-x-auto">
          <div
            v-for="prayer in visiblePrayerRows"
            :key="prayer.key"
            class="schedule-grid border-b border-slate-100 min-h-[120px]"
          >
            <div class="p-4 border-l border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1">
              <span class="text-sm font-bold">{{ prayer.label }}</span>
            </div>

            <div
              v-for="weekday in weekdays"
              :key="`${prayer.key}-${weekday.id}`"
              class="p-2 border-l border-slate-100"
            >
              <div class="space-y-2">
                <div
                  v-for="lesson in lessonsByPrayerAndDay(prayer.key, weekday.id)"
                  :key="lesson.id"
                  class="p-3 rounded-lg h-full border-r-4 overflow-hidden min-w-0"
                  :class="lessonColorClass(lesson.color)"
                >
                  <p class="lesson-card-title mb-1">{{ lesson.subject }}</p>
                  <p class="lesson-card-line text-slate-700 mb-1">{{ lesson.teacher }}</p>
                  <div class="lesson-card-line text-slate-600">
                    <span class="material-icons-round lesson-card-icon">schedule</span>
                    {{ lessonTimeLabel(lesson) }}
                  </div>
                  <p class="lesson-card-line text-slate-500 mt-1">
                    {{ lessonActualTimeLabel(lesson) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!selectedStudent" class="p-8 text-center text-slate-500">
            اختر طالبًا أولاً لعرض جدول الأسبوع.
          </div>
          <div v-else-if="!filteredLessons.length" class="p-8 text-center text-slate-500">
            لا توجد فترات للطالب المحدد ضمن الفلاتر الحالية.
          </div>
        </div>
      </div>
      <!----
      <div class="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <button
            @click="goPreviousWeek"
            class="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            <span class="material-icons-round">chevron_right</span>
          </button>
          <span class="text-sm font-medium">الأسبوع الحالي: {{ weekLabel }}</span>
          <button
            @click="goNextWeek"
            class="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            <span class="material-icons-round">chevron_left</span>
          </button>
        </div>
      </div>
      --->
    </section>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import {
  getIqraStudentWeeklySchedule,
  listIqraSchedules,
  listIqraSlotTeachers,
  listIqraStudents,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraStudentWeeklySchedule',
  data() {
    return {
      loading: false,
      errorMessage: '',
      searchQuery: '',
      studentSearchQuery: '',
      selectedSchedule: '',
      selectedTeacher: '',
      selectedStudent: '',
      studentSearchDebounceId: null,
      weekOffset: 0,
      weekdays: [
        { id: 7, label: 'الأحد' },
        { id: 1, label: 'الإثنين' },
        { id: 2, label: 'الثلاثاء' },
        { id: 3, label: 'الأربعاء' },
        { id: 4, label: 'الخميس' },
        { id: 5, label: 'الجمعة' },
        { id: 6, label: 'السبت' },
      ],
      prayerRows: [
        { key: 'fajr', label: 'صلاة الفجر', order: 1 },
        { key: 'dhuhr', label: 'صلاة الظهر', order: 2 },
        { key: 'asr', label: 'صلاة العصر', order: 3 },
        { key: 'maghrib', label: 'صلاة المغرب', order: 4 },
        { key: 'isha', label: 'صلاة العشاء', order: 5 },
      ],
      lessons: [],
      studentOptions: [],
      scheduleOptions: [],
      teacherOptions: [],
    };
  },
  async mounted() {
    await this.loadInitialData();
  },
  beforeUnmount() {
    if (this.studentSearchDebounceId) {
      clearTimeout(this.studentSearchDebounceId);
    }
  },
  computed: {
    selectedStudentName() {
      const current = this.studentOptions.find((item) => String(item.id) === String(this.selectedStudent));
      return current?.full_name || '';
    },
    filteredLessons() {
      const search = this.searchQuery.trim().toLowerCase();
      return this.lessons.filter((lesson) => !search
        || lesson.subject.toLowerCase().includes(search)
        || lesson.teacher.toLowerCase().includes(search)
        || lesson.scheduleTitle.toLowerCase().includes(search));
    },
    visiblePrayerRows() {
      const rows = [...this.prayerRows];
      const hasFixedRows = this.filteredLessons.some((lesson) => lesson.prayerKey === 'fixed');
      if (hasFixedRows) {
        rows.push({ key: 'fixed', label: 'أوقات ثابتة', order: 6 });
      }
      return rows.sort((a, b) => a.order - b.order);
    },
    totalHoursPerWeek() {
      const totalMinutes = this.filteredLessons.reduce((sum, lesson) => sum + (lesson.durationMinutes || 0), 0);
      return totalMinutes / 60;
    },
    weekLabel() {
      const sunday = this.getWeekStartDate();
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);

      const formatDate = (date) => date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' });
      return `${formatDate(sunday)} - ${formatDate(saturday)}`;
    },
  },
  methods: {
    async loadInitialData() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const [schedulesResult] = await Promise.all([
          listIqraSchedules({
            is_active: true,
            page: 1,
            size: 100,
          }),
        ]);

        this.scheduleOptions = (schedulesResult?.data || []).map((schedule) => ({
          id: String(schedule.id),
          title: schedule.title || `جدول #${schedule.id}`,
        }));

        await this.loadTeachers();
        await this.loadStudents();

        if (this.studentOptions.length && !this.selectedStudent) {
          this.selectedStudent = String(this.studentOptions[0].id);
        }

        await this.loadStudentSchedule();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل بيانات جدول الطلاب';
      } finally {
        this.loading = false;
      }
    },
    async loadTeachers() {
      const params = { is_active: true };
      if (this.selectedSchedule) {
        params.schedule_id = Number(this.selectedSchedule);
      }
      const slotTeachers = await listIqraSlotTeachers(params);
      const unique = new Map();
      (slotTeachers || []).forEach((item) => {
        if (!item || !item.teacher_id) return;
        const id = String(item.teacher_id);
        if (unique.has(id)) return;
        unique.set(id, {
          id,
          name: item.teacher_name || `#${item.teacher_id}`,
        });
      });
      this.teacherOptions = Array.from(unique.values())
        .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    },
    async loadStudents() {
      const query = this.studentSearchQuery.trim();
      const params = {
        q: query || undefined,
        page: 1,
        size: 200,
      };
      if (this.selectedSchedule) {
        params.schedule_id = Number(this.selectedSchedule);
      }
      if (this.selectedTeacher) {
        params.teacher_id = Number(this.selectedTeacher);
      }
      const studentsResult = await listIqraStudents(params);
      this.studentOptions = studentsResult?.data || [];
    },
    async reloadStudentsAndSync() {
      await this.loadStudents();
      if (!this.studentOptions.some((item) => String(item.id) === String(this.selectedStudent))) {
        this.selectedStudent = this.studentOptions.length ? String(this.studentOptions[0].id) : '';
      }
    },
    async loadStudentSchedule() {
      if (!this.selectedStudent) {
        this.lessons = [];
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        const params = {};
        if (this.selectedSchedule) {
          params.schedule_id = Number(this.selectedSchedule);
        }
        params.week_start_date = this.formatDateIso(this.getWeekStartDate());
        const result = await getIqraStudentWeeklySchedule(this.selectedStudent, params);
        const rawLessons = result?.lessons || [];

        this.lessons = rawLessons.map((lesson) => ({
          id: `${lesson.id}-${lesson.slot_id}`,
          scheduleId: String(lesson.schedule_id),
          weekday: Number(lesson.weekday),
          slotId: Number(lesson.slot_id),
          subject: lesson.slot_name || 'فترة الجلسة',
          teacher: lesson.teacher_name || 'غير محدد',
          scheduleTitle: lesson.schedule_title || '',
          slotOrder: Number(lesson.slot_order) || 0,
          startMode: lesson.start_mode,
          fixedStartTime: lesson.fixed_start_time,
          prayerName: lesson.prayer_name,
          prayerKey: this.normalizePrayerName(lesson.prayer_name),
          offsetMinutes: lesson.offset_minutes,
          durationMinutes: Number(lesson.duration_minutes) || 0,
          actualStartTime: lesson.actual_start_time ? String(lesson.actual_start_time).slice(0, 8) : '',
          actualEndTime: lesson.actual_end_time ? String(lesson.actual_end_time).slice(0, 8) : '',
          prayerSource: lesson.prayer_source || '',
          color: this.colorFromName(lesson.teacher_name || ''),
        }));
      } catch (error) {
        this.lessons = [];
        this.errorMessage = error?.message || 'فشل في تحميل جدول الطالب الأسبوعي';
      } finally {
        this.loading = false;
      }
    },
    lessonsByPrayerAndDay(prayerKey, weekday) {
      return this.filteredLessons
        .filter((lesson) => lesson.prayerKey === prayerKey && lesson.weekday === weekday)
        .sort((a, b) => this.lessonSortWeight(a) - this.lessonSortWeight(b));
    },
    getWeekStartDate() {
      const now = new Date();
      const currentDay = now.getDay();
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - currentDay + this.weekOffset * 7);
      sunday.setHours(0, 0, 0, 0);
      return sunday;
    },
    formatDateIso(date) {
      const value = new Date(date);
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    normalizePrayerName(prayerName) {
      const normalized = String(prayerName || '').trim().toLowerCase();
      if (['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].includes(normalized)) {
        return normalized;
      }
      return 'fixed';
    },
    lessonSortWeight(lesson) {
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        const [hoursText, minutesText] = String(lesson.fixedStartTime).split(':');
        const hours = Number(hoursText);
        const minutes = Number(minutesText);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          return (hours * 60) + minutes;
        }
      }
      const offset = Number(lesson.offsetMinutes);
      if (!Number.isNaN(offset)) {
        return offset;
      }
      return Number(lesson.slotOrder) || Number(lesson.slotId) || 0;
    },
    lessonTimeLabel(lesson) {
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        return this.formatTimeArabic(lesson.fixedStartTime);
      }

      if (lesson.startMode === 'prayer_offset') {
        const prayerLabel = this.prayerLabel(lesson.prayerName || lesson.prayerKey);
        const offset = Number(lesson.offsetMinutes);

        if (Number.isNaN(offset) || offset === 0) {
          return `وقت ${prayerLabel}`;
        }

        const direction = offset > 0 ? 'بعد' : 'قبل';
        return `${direction} ${prayerLabel} بـ ${Math.abs(offset)} دقيقة`;
      }

      return `الفترة ${lesson.slotOrder || lesson.slotId}`;
    },
    lessonActualTimeLabel(lesson) {
      const actual = this.lessonActualTime(lesson);
      if (!actual) return 'الوقت الفعلي: غير متاح';
      return `الوقت الفعلي: ${this.formatTimeArabic(actual)}`;
    },
    lessonActualTime(lesson) {
      if (lesson.actualStartTime) {
        return String(lesson.actualStartTime).slice(0, 8);
      }
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        return String(lesson.fixedStartTime).slice(0, 8);
      }
      return '';
    },
    formatTimeArabic(timeValue) {
      const value = String(timeValue || '').slice(0, 5);
      const [hoursText, minutesText] = value.split(':');
      const hours = Number(hoursText);
      const minutes = Number(minutesText);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return value || '-';

      const period = hours >= 12 ? 'م' : 'ص';
      const hour12 = hours % 12 === 0 ? 12 : hours % 12;
      return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
    },
    prayerLabel(prayerValue) {
      const key = String(prayerValue || '').trim().toLowerCase();
      const map = {
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      };
      return map[key] || 'الصلاة';
    },
    slotStartLabel(lesson) {
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        return String(lesson.fixedStartTime).slice(0, 5);
      }
      if (lesson.startMode === 'prayer_offset' && lesson.prayerName) {
        return `صلاة ${lesson.prayerName}`;
      }
      return `الفترة ${lesson.slotOrder || lesson.slotId}`;
    },
    slotEndLabel(lesson) {
      const duration = Number(lesson.durationMinutes) || 0;
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime && duration > 0) {
        return this.addMinutes(String(lesson.fixedStartTime).slice(0, 5), duration);
      }
      if (duration > 0) {
        return `${duration} دقيقة`;
      }
      return '-';
    },
    addMinutes(timeValue, minutesToAdd) {
      const [hoursText, minutesText] = String(timeValue).split(':');
      const hours = Number(hoursText);
      const minutes = Number(minutesText);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeValue;
      const total = (hours * 60) + minutes + minutesToAdd;
      const normalized = ((total % 1440) + 1440) % 1440;
      const outHours = String(Math.floor(normalized / 60)).padStart(2, '0');
      const outMinutes = String(normalized % 60).padStart(2, '0');
      return `${outHours}:${outMinutes}`;
    },
    colorFromName(name) {
      const palette = ['blue', 'amber', 'emerald', 'purple', 'indigo', 'rose'];
      const value = String(name || '');
      let hash = 0;
      for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) - hash) + value.charCodeAt(i);
        hash |= 0;
      }
      const index = Math.abs(hash) % palette.length;
      return palette[index];
    },
    lessonColorClass(color) {
      const map = {
        blue: 'bg-blue-50 border-blue-500 text-blue-700',
        amber: 'bg-amber-50 border-amber-500 text-amber-700',
        emerald: 'bg-emerald-50 border-emerald-500 text-emerald-700',
        purple: 'bg-purple-50 border-purple-500 text-purple-700',
        indigo: 'bg-indigo-50 border-indigo-500 text-indigo-700',
        rose: 'bg-rose-50 border-rose-500 text-rose-700',
      };
      return map[color] || map.blue;
    },
    goPreviousWeek() {
      this.weekOffset -= 1;
    },
    goNextWeek() {
      this.weekOffset += 1;
    },
  },
  watch: {
    selectedStudent() {
      this.loadStudentSchedule();
    },
    async selectedSchedule() {
      try {
        this.selectedTeacher = '';
        await this.loadTeachers();
        await this.reloadStudentsAndSync();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل بيانات الجدول';
      }
      this.loadStudentSchedule();
    },
    async selectedTeacher() {
      try {
        await this.reloadStudentsAndSync();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل طلاب المعلم';
      }
    },
    weekOffset() {
      this.loadStudentSchedule();
    },
    studentSearchQuery() {
      if (this.studentSearchDebounceId) {
        clearTimeout(this.studentSearchDebounceId);
      }
      this.studentSearchDebounceId = setTimeout(async () => {
        try {
          await this.reloadStudentsAndSync();
        } catch (error) {
          this.errorMessage = error?.message || 'فشل في البحث عن الطلاب';
        }
      }, 350);
    },
  },
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Round');

.material-icons-round {
  font-size: 20px;
}

.schedule-grid {
  display: grid;
  grid-template-columns: 100px repeat(7, minmax(150px, 1fr));
}

.lesson-card-title {
  font-size: clamp(10px, 1.1vw, 12px);
  line-height: 1.25;
  font-weight: 700;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.lesson-card-line {
  min-width: 0;
  font-size: clamp(9px, 1vw, 11px);
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.lesson-card-icon {
  font-size: 13px;
  flex: 0 0 auto;
  margin-top: 1px;
}

main {
  font-family: 'Noto Kufi Arabic', sans-serif;
}
</style>
